import React, {
  createContext,
  useEffect,
  useMemo,
  useState,
  useContext,
  useCallback,
} from 'react';

type State = Record<string, string>;

type Ctx = {
  state: State;
  setTerm: (key: string, value: string) => void;
  clearTerm: (key: string) => void;
};

const BuscaContext = createContext<Ctx | undefined>(undefined);

interface BuscaProviderProps {
  children: React.ReactNode;
  storageKey?: string;
}

const areStatesEqual = (a: State, b: State) => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  if (aKeys.length !== bKeys.length) return false;

  for (const key of aKeys) {
    if (a[key] !== b[key]) return false;
  }

  return true;
};

export const BuscaProvider: React.FC<BuscaProviderProps> = ({
  children,
  storageKey = 'buscas',
}) => {
  const storageApi = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    try {
      return window.sessionStorage;
    } catch {
      return undefined;
    }
  }, []);

  const [state, setState] = useState<State>(() => {
    try {
      const saved = storageApi?.getItem(storageKey);
      return saved ? (JSON.parse(saved) as State) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      storageApi?.setItem(storageKey, JSON.stringify(state));
    } catch {}
  }, [state, storageApi, storageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (e: StorageEvent) => {
      if (!e.key || e.key !== storageKey) return;
      try {
        const next = e.newValue ? (JSON.parse(e.newValue) as State) : {};
        setState((prev) => (areStatesEqual(prev, next) ? prev : next));
      } catch {}
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [storageKey]);

  const setTerm = useCallback((key: string, value: string) => {
    setState((prev) => {
      if (prev[key] === value) return prev;
      return { ...prev, [key]: value };
    });
  }, []);

  const clearTerm = useCallback((key: string) => {
    setState((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const value = useMemo<Ctx>(
    () => ({
      state,
      setTerm,
      clearTerm,
    }),
    [state, setTerm, clearTerm],
  );

  return (
    <BuscaContext.Provider value={value}>{children}</BuscaContext.Provider>
  );
};

export const useBusca = (key: string) => {
  const ctx = useContext(BuscaContext);
  if (!ctx) throw new Error('useBusca deve ser usado dentro de BuscaProvider');

  const busca = ctx.state[key] ?? '';

  const setBusca = useCallback(
    (value: string) => {
      ctx.setTerm(key, value);
    },
    [ctx, key],
  );

  const limparBusca = useCallback(() => {
    ctx.clearTerm(key);
  }, [ctx, key]);

  return useMemo(
    () => ({
      busca,
      setBusca,
      limparBusca,
    }),
    [busca, setBusca, limparBusca],
  );
};
